import { useCallback, useId, useRef, useState, type ReactNode } from 'react';
import { MOTION } from '@constants/motion';
import { COMMON } from '@locales/id/messages';
import { useBodyClass } from '@lib/hooks';
import { prefersReducedMotion } from '@lib/utils/motion';
import { IconButton } from './IconButton';
import { useExitGhost } from './useExitGhost';
import { useModalFocus } from './useModalFocus';

interface Props {
  title: ReactNode;
  sub?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** a function gets the drawer's own close (with its slide-out and `beforeClose`), for a Batal button */
  footer?: ReactNode | ((close: () => void) => ReactNode);
  returnTo?: () => HTMLElement | null;
  label?: string;
  /** `wide`: a form with a map (full screen on phones instead of a sheet) */
  size?: 'wide';
  /** false (saving): Esc, the backdrop and X do nothing */
  dismissible?: boolean;
  /** asked before closing (a form with unsaved changes asks the person); false keeps the drawer open */
  beforeClose?: () => boolean | Promise<boolean>;
}

/**
 * Detail panel over the page: slides in from the right on laptops, up from the bottom on phones (a sheet; `wide`:
 * the whole screen). Modal like a Dialog (focus inside, Esc and the backdrop close it, focus returns to `returnTo()`
 * or what had it); it slides out before `onClose` runs (at once under reduced motion). As in a Dialog, once something
 * was typed or chosen inside, a tap on the backdrop no longer closes it; Esc and X still do.
 */
export function Drawer({ title, sub, onClose, children, footer, returnTo, label, size, dismissible = true, beforeClose }: Props) {
  const ref = useRef<HTMLElement>(null);
  const back = useRef<HTMLDivElement>(null);
  // closed by its caller at once (a sheet's "Lihat hasil"): it still slides away
  useExitGhost(back, 'drawer');
  const titleId = useId();
  const [leaving, setLeaving] = useState(false);
  const closing = useRef(false);
  const done = useRef(onClose);
  done.current = onClose;
  const canDismiss = useRef(dismissible);
  canDismiss.current = dismissible;
  const edited = useRef(false);
  const ask = useRef(beforeClose);
  ask.current = beforeClose;
  const close = useCallback(async () => {
    if (closing.current || !canDismiss.current) return;
    closing.current = true;
    if (ask.current && !(await ask.current())) {
      closing.current = false;
      return;
    }
    setLeaving(true);
    window.setTimeout(() => done.current(), prefersReducedMotion() ? 0 : MOTION.drawerCloseMs);
  }, []);
  useBodyClass('no-scroll');
  useModalFocus(ref, () => void close(), returnTo);
  return (
    <div ref={back} className={`drawer-back ${leaving ? 'leaving' : ''}`} onMouseDown={(e) => e.target === e.currentTarget && !edited.current && void close()}>
      <aside className={`drawer ${size === 'wide' ? 'drawer-wide' : ''} ${leaving ? 'leaving' : ''}`} role="dialog" aria-modal="true" aria-labelledby={label ? undefined : titleId} aria-label={label} ref={ref} onInput={() => (edited.current = true)}>
        <span className="drawer-grip" aria-hidden="true" />
        <header className="drawer-head">
          <div className="drawer-title">
            <h2 id={titleId}>{title}</h2>
            {sub && <div className="drawer-sub">{sub}</div>}
          </div>
          <IconButton icon="close" label={COMMON.close} onClick={() => void close()} disabled={!dismissible} />
        </header>
        <div className="drawer-body">{children}</div>
        {footer && <footer className="drawer-foot">{typeof footer === 'function' ? footer(() => void close()) : footer}</footer>}
      </aside>
    </div>
  );
}
